'use client'

import { FormField } from '@/components/form-section'
import SalesTaskFinishModal, { type SalesTaskFinishAction } from '@/components/sales/SalesTaskFinishModal'
import {
    createSalesTask,
    getSalesTasks,
    rescheduleSalesTask,
    type SalesTask,
    type SalesTaskType,
} from '@/lib/api/sales/tasks-api'
import { getOrganizationUsers, type OrganizationUser } from '@/lib/api/users/users-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdAdd, MdAssignment, MdCheck, MdClose, MdEventRepeat } from 'react-icons/md'
import { toast } from 'react-toastify'

const TASK_TYPE_LABELS: Record<SalesTaskType, string> = {
    TASK_CALL: 'Ligar',
    TASK_FOLLOW_UP: 'Acompanhar',
    TASK_MEETING: 'Reunião',
    TASK_SEND_QUOTE: 'Enviar orçamento',
    TASK_OTHER: 'Outra tarefa',
}

const OPEN_STATUSES = ['PENDING', 'CONFIRMED', 'OVERDUE']

const pad = (n: number) => String(n).padStart(2, '0')
/** `YYYY-MM-DDTHH:mm` local, formato do `datetime-local`. */
const toLocalInput = (date: Date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`

const formatDue = (task: SalesTask) => {
    const date = new Date(task.startDate)
    const day = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    return task.allDay ? `${day}, dia inteiro` : `${day} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

const isOverdue = (task: SalesTask) =>
    task.status === 'OVERDUE' || (OPEN_STATUSES.includes(task.status) && new Date(task.endDate).getTime() < Date.now())

type Finishing = { task: SalesTask; action: SalesTaskFinishAction }

/**
 * Tarefas do lead ou da oportunidade. A tarefa vai para a agenda pessoal do responsável:
 * aparece no calendário, fica atrasada sozinha e lembra no sino (docs/sales-tasks.md).
 */
export default function SalesTasksPanel({ leadId, dealId, defaultAssigneeId }: { leadId?: number; dealId?: number; defaultAssigneeId?: number | null }) {
    const { canUpdate } = useResourcePermissions('sales', 'leads')
    const [tasks, setTasks] = useState<SalesTask[]>([])
    const [loading, setLoading] = useState(true)
    const [users, setUsers] = useState<OrganizationUser[]>([])
    const [showForm, setShowForm] = useState(false)
    const [saving, setSaving] = useState(false)
    const [showDone, setShowDone] = useState(false)
    const [finishing, setFinishing] = useState<Finishing | null>(null)
    const [rescheduling, setRescheduling] = useState<SalesTask | null>(null)
    const [newDue, setNewDue] = useState('')

    const emptyForm = () => {
        const due = new Date(Date.now() + 60 * 60_000)
        due.setMinutes(0, 0, 0)
        return { type: 'TASK_CALL' as SalesTaskType, title: '', due: toLocalInput(due), allDay: false, assignedUserId: defaultAssigneeId ?? null as number | null, description: '' }
    }
    const [form, setForm] = useState(emptyForm)

    const load = useCallback(async () => {
        try {
            const res = await getSalesTasks({ leadId, dealId })
            if (res.success && Array.isArray(res.result)) setTasks(res.result)
            else if (!res.success) toast.error(res.error || 'Não foi possível carregar as tarefas')
        } catch {
            toast.error('Não foi possível carregar as tarefas')
        } finally {
            setLoading(false)
        }
    }, [leadId, dealId])

    useEffect(() => {
        load()
    }, [load])

    useEffect(() => {
        if (!showForm || users.length) return
        getOrganizationUsers()
            .then(res => res.success && Array.isArray(res.result) && setUsers(res.result))
            .catch(() => {})
    }, [showForm, users.length])

    const openTasks = useMemo(() => tasks.filter(task => OPEN_STATUSES.includes(task.status)), [tasks])
    const doneTasks = useMemo(() => tasks.filter(task => !OPEN_STATUSES.includes(task.status)).reverse(), [tasks])

    const handleCreate = async () => {
        if (!form.title.trim() || !form.due) return
        setSaving(true)
        try {
            const res = await createSalesTask({
                type: form.type,
                title: form.title.trim(),
                description: form.description.trim() || undefined,
                startDate: new Date(form.due).toISOString(),
                allDay: form.allDay,
                leadId,
                dealId,
                assignedUserId: form.assignedUserId ?? undefined,
            })
            if (res.success) {
                toast.success('Tarefa criada')
                setShowForm(false)
                setForm(emptyForm())
                load()
            } else {
                toast.error(res.error || 'Não foi possível criar a tarefa')
            }
        } catch {
            toast.error('Não foi possível criar a tarefa')
        } finally {
            setSaving(false)
        }
    }

    const handleReschedule = async () => {
        if (!rescheduling || !newDue) return
        setSaving(true)
        try {
            const res = await rescheduleSalesTask(rescheduling.id, { startDate: new Date(newDue).toISOString() })
            if (res.success) {
                toast.success('Prazo alterado')
                setRescheduling(null)
                load()
            } else {
                toast.error(res.error || 'Não foi possível mudar o prazo')
            }
        } catch {
            toast.error('Não foi possível mudar o prazo')
        } finally {
            setSaving(false)
        }
    }

    const renderTask = (task: SalesTask) => {
        const open = OPEN_STATUSES.includes(task.status)
        const late = open && isOverdue(task)
        return (
            <li key={task.id} className={`flex flex-wrap items-start gap-3 rounded-lg border px-4 py-3 ${late ? 'border-error/40 bg-error/5' : 'border-base-300'}`}>
                <MdAssignment className={`mt-0.5 shrink-0 text-lg ${late ? 'text-error' : open ? 'text-secondary' : 'text-base-content/40'}`} />
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="badge badge-ghost badge-sm">{TASK_TYPE_LABELS[task.type] ?? 'Tarefa'}</span>
                        <span className={`text-sm font-medium ${open ? '' : 'line-through text-base-content/50'}`}>{task.title}</span>
                        {late && <span className="badge badge-error badge-sm">Atrasada</span>}
                        {task.status === 'COMPLETED' && <span className="badge badge-success badge-sm">Concluída</span>}
                        {task.status === 'CANCELED' && <span className="badge badge-ghost badge-sm">Cancelada</span>}
                    </div>
                    <p className="mt-1 text-xs text-base-content/60">
                        {formatDue(task)}
                        {task.agenda.user?.name ? ` · ${task.agenda.user.name}` : ''}
                    </p>
                    {task.description && <p className="mt-1 whitespace-pre-wrap text-xs text-base-content/70">{task.description}</p>}
                    {task.notes && !open && <p className="mt-1 whitespace-pre-wrap text-xs text-base-content/70">Resultado: {task.notes}</p>}
                </div>
                {open && canUpdate && (
                    <div className="flex flex-wrap gap-1">
                        <button type="button" className="btn btn-success btn-xs gap-1" onClick={() => setFinishing({ task, action: 'complete' })}>
                            <MdCheck /> Concluir
                        </button>
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs gap-1"
                            onClick={() => {
                                setRescheduling(task)
                                setNewDue(toLocalInput(new Date(task.startDate)))
                            }}
                        >
                            <MdEventRepeat /> Mudar prazo
                        </button>
                        <button type="button" className="btn btn-ghost btn-xs gap-1 text-error" onClick={() => setFinishing({ task, action: 'cancel' })}>
                            <MdClose /> Cancelar
                        </button>
                    </div>
                )}
            </li>
        )
    }

    return (
        <div className="card bg-base-100 shadow-sm">
            <div className="card-body gap-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h3 className="card-title text-base">
                            <MdAssignment size={18} /> Tarefas
                        </h3>
                        <p className="text-xs text-base-content/60">Ficam na agenda pessoal do responsável, com lembrete no sino.</p>
                    </div>
                    {canUpdate && !showForm && (
                        <button type="button" className="btn btn-secondary btn-sm gap-1" onClick={() => setShowForm(true)}>
                            <MdAdd /> Nova tarefa
                        </button>
                    )}
                </div>

                {showForm && (
                    <div className="rounded-xl border border-base-300 bg-base-200/40 p-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <FormField label="Tipo" htmlFor="task-type" required>
                                <select id="task-type" className="select select-bordered select-sm w-full" value={form.type} onChange={e => setForm({ ...form, type: e.target.value as SalesTaskType })}>
                                    {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </FormField>
                            <FormField label="Responsável" htmlFor="task-assignee" hint={form.assignedUserId ? undefined : 'Vazio: o responsável pelo lead, senão você.'}>
                                <select
                                    id="task-assignee"
                                    className="select select-bordered select-sm w-full"
                                    value={form.assignedUserId ?? ''}
                                    onChange={e => setForm({ ...form, assignedUserId: e.target.value ? Number(e.target.value) : null })}
                                >
                                    <option value="">Responsável pelo lead</option>
                                    {users.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </select>
                            </FormField>
                            <FormField label="O que fazer" htmlFor="task-title" required className="sm:col-span-2">
                                <input
                                    id="task-title"
                                    className="input input-bordered input-sm w-full"
                                    placeholder="Ex.: Ligar para confirmar o interesse no exame"
                                    maxLength={200}
                                    value={form.title}
                                    onChange={e => setForm({ ...form, title: e.target.value })}
                                />
                            </FormField>
                            <FormField label="Prazo" htmlFor="task-due" required>
                                <input
                                    id="task-due"
                                    type={form.allDay ? 'date' : 'datetime-local'}
                                    className="input input-bordered input-sm w-full"
                                    value={form.allDay ? form.due.slice(0, 10) : form.due}
                                    onChange={e => setForm({ ...form, due: form.allDay ? `${e.target.value}T09:00` : e.target.value })}
                                />
                            </FormField>
                            <FormField label="Horário" htmlFor="task-all-day">
                                <label className="flex h-8 cursor-pointer items-center gap-2 text-sm">
                                    <input id="task-all-day" type="checkbox" className="toggle toggle-sm toggle-secondary" checked={form.allDay} onChange={e => setForm({ ...form, allDay: e.target.checked })} />
                                    Dia inteiro, sem hora marcada
                                </label>
                            </FormField>
                            <FormField label="Detalhes" htmlFor="task-description" optional className="sm:col-span-2">
                                <textarea
                                    id="task-description"
                                    className="textarea textarea-bordered w-full text-sm"
                                    rows={2}
                                    value={form.description}
                                    onChange={e => setForm({ ...form, description: e.target.value })}
                                />
                            </FormField>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)} disabled={saving}>
                                Cancelar
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={handleCreate} disabled={saving || !form.title.trim() || !form.due}>
                                {saving ? <span className="loading loading-spinner loading-xs" /> : 'Criar tarefa'}
                            </button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center py-6">
                        <span className="loading loading-spinner loading-md" />
                    </div>
                ) : openTasks.length === 0 ? (
                    <p className="py-4 text-center text-sm text-base-content/50">Nenhuma tarefa em aberto.</p>
                ) : (
                    <ul className="flex flex-col gap-2">{openTasks.map(renderTask)}</ul>
                )}

                {doneTasks.length > 0 && (
                    <div>
                        <button type="button" className="btn btn-ghost btn-xs" onClick={() => setShowDone(!showDone)}>
                            {showDone ? 'Ocultar' : 'Mostrar'} encerradas ({doneTasks.length})
                        </button>
                        {showDone && <ul className="mt-2 flex flex-col gap-2">{doneTasks.map(renderTask)}</ul>}
                    </div>
                )}
            </div>

            {finishing && (
                <SalesTaskFinishModal
                    task={finishing.task}
                    action={finishing.action}
                    onClose={finished => {
                        setFinishing(null)
                        if (finished) load()
                    }}
                />
            )}

            {rescheduling && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-md">
                        <h3 className="mb-1 text-lg font-bold">Mudar prazo</h3>
                        <p className="mb-4 text-sm text-base-content/60">{rescheduling.title}</p>
                        <FormField label="Novo prazo" htmlFor="task-new-due" required hint={rescheduling.status === 'OVERDUE' ? 'A tarefa deixa de estar atrasada.' : undefined}>
                            <input id="task-new-due" type="datetime-local" className="input input-bordered input-sm w-full" value={newDue} onChange={e => setNewDue(e.target.value)} />
                        </FormField>
                        <div className="modal-action">
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRescheduling(null)} disabled={saving}>
                                Voltar
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={handleReschedule} disabled={saving || !newDue}>
                                {saving ? <span className="loading loading-spinner loading-xs" /> : 'Salvar prazo'}
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={() => !saving && setRescheduling(null)} />
                </div>
            )}
        </div>
    )
}
